import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiControlledAutocompleteOptionProps,
  MiPopper,
} from "@miplatform/components";
import { HOME_PHASE, TYPOGRAPHY_VARIANTS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { MenuItem, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { getHomePhase } from "../../../utils/getHomePhase/getHomePhase";
import { useHomeSearchOption } from "../hooks/useHomeSearchOption";
import { HomePopperContent } from "./HomePopperContent";

const HOME_PHASE_MAP = {
  [HOME_PHASE.UNDER_CONSTRUCTION]: {
    text: "Under Construction",
    color: "primary" as const,
  },
  [HOME_PHASE.UNDER_WARRANTY]: {
    text: "Under Warranty",
    color: "success" as const,
  },
  [HOME_PHASE.OUT_OF_WARRANTY]: {
    text: "Out of Warranty",
    color: "error" as const,
  },
};

export function HomeSearchOption({
  option,
  props,
  close,
}: HomeSearchOptionProps) {
  const { push } = useRouter();
  const phase = getHomePhase(option);
  const { text: phaseText, color } = HOME_PHASE_MAP[phase];
  const { classes, theme } = useStyles();

  const [timeoutId, setTimeoutId] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const { orders, currentOrder, startDate, completeDate, getOrders } =
    useHomeSearchOption(option);

  const openPopper = (e: React.MouseEvent<HTMLElement>) => {
    const { currentTarget } = e;
    const timeout = setTimeout(() => {
      if (!anchorEl) {
        setAnchorEl(currentTarget);
      }
      if (!orders) {
        getOrders(option.id);
      }
    }, 250);
    setTimeoutId(timeout);
  };

  const closePopper = () => {
    setAnchorEl(null);
    if (timeoutId) {
      clearTimeout(timeoutId);
      setTimeoutId(null);
    }
  };

  const handleOnClick = (event: React.MouseEvent<HTMLLIElement>) => {
    event.preventDefault();
    event.stopPropagation();
    push(`/homes/${option.id}`);
    closePopper();
    close();
  };

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return (
    <MenuItem
      {...props}
      onClick={handleOnClick}
      className={classes.menuItem}
      onMouseEnter={openPopper}
      onMouseLeave={closePopper}
    >
      <Box className={classes.contentBox}>
        <Typography
          className={classes.address}
          variant={TYPOGRAPHY_VARIANTS.BD2}
        >
          {option.address?.addressLine1}
        </Typography>
        <Typography variant={TYPOGRAPHY_VARIANTS.BD2}>
          {option.accountType}
        </Typography>
        <Typography
          className={option.builder ? classes.borderLeft : ""}
          variant={TYPOGRAPHY_VARIANTS.BD2}
        >
          {option.builder}
        </Typography>
        <Typography
          variant={TYPOGRAPHY_VARIANTS.BD2}
          className={option.community ? classes.borderLeft : ""}
        >
          {option.community}
        </Typography>
        <MiPopper
          anchorEl={anchorEl}
          arrowColor={theme.palette[color].main}
          showArrow
          open={Boolean(anchorEl)}
          placement="right"
          sx={{
            border: `1px solid ${theme.palette[color].main}`,
            borderRadius: theme.spacing(2),
          }}
        >
          <HomePopperContent
            home={option}
            orders={orders}
            currentOrder={currentOrder}
            startDate={startDate}
            completeDate={completeDate}
            phaseText={phaseText}
            color={color}
            closeMenu={close}
          />
        </MiPopper>
      </Box>
    </MenuItem>
  );
}

interface HomeSearchOptionData {
  id: string;
  address?: { addressLine1?: string; city?: string };
  accountType?: string;
  builder?: string;
  community?: string;
}

export type HomeSearchOptionProps =
  MiControlledAutocompleteOptionProps<HomeSearchOptionData>;

const useStyles = makeStyles()((theme) => ({
  borderLeft: {
    paddingLeft: theme.spacing(2),
    borderLeft: `1px solid ${theme.palette.bluegrey.main}`,
  },
  menuItem: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    whiteSpace: "nowrap",
    overflow: "hidden",
    width: "100%",
    padding: theme.spacing(1, 0),
  },
  address: {
    color: theme.palette.common.black,
  },
  contentBox: {
    gap: theme.spacing(2),
    display: "flex",
    width: "100%",
    height: "100%",
    padding: theme.spacing(1),
  },
}));
